import { useState, useCallback, useEffect } from 'react'
import { useAuthSettings, BEARER_PREFIX } from '../authorization'
import { GA_SEND_EVENT } from '../../constants/googleAnalytics'
import parseJWT from 'jwt-decode'

import { getIsHomebotDomain } from '../../helpers/browser'
import { useWidgetMode, useIsExtension } from '../utility'
import { sendMessage } from '../../helpers/communication/serviceWorker.js'
import { postGAEvent } from '../../helpers/googleAnalytics'

export const useLogGAEvent = (name, { debounceInterval } = {}) => {
  const [token, setToken] = useState(null)
  const [bounceTimer, setBounceTimer] = useState(null)
  const [queuedArgs, setQueuedArgs] = useState(null)

  const widgetMode = useWidgetMode()
  const isExtension = useIsExtension()
  const authSettings = useAuthSettings()

  if (!token && authSettings?.scheme === BEARER_PREFIX && authSettings?.token) {
    try {
      const jwt = parseJWT(authSettings.token)
      setToken(jwt)
    } catch (e) {
      console.warn('Unable to parse provided JWT')
    }
  }

  const execute = useCallback(
    (params = {}) => {
      let clientId
      const event = {
        name,
        params: {
          widgetMode,
          ...params,
        },
      }

      if (token) {
        const tokenValues = token.acr?.values
        const clientValue =
          tokenValues && tokenValues.length ? tokenValues[0].context : null

        if (clientValue.replace) {
          clientId = clientValue.replace('client:', '')
        }
      }

      const sendArgs = {
        clientId,
        event,
      }

      if (getIsHomebotDomain()) {
        postGAEvent(sendArgs)
      } else if (isExtension) {
        // Don't post apiSecrets and GA stuff from extension DOM, send via background service worker
        sendMessage({
          ...sendArgs,
          type: GA_SEND_EVENT,
        })
      } else {
        console.warn(
          'WARNING: GA events will not be logged unless built with CHROME_EXTENSION widget mode or we are on a Homebot domain'
        )
      }
    },
    [token, name, widgetMode, isExtension]
  )

  const fireAndForget = useCallback(
    (...args) => {
      if (!token && !queuedArgs) {
        setQueuedArgs(args)
      } else if (!bounceTimer) {
        execute(...args)

        if (debounceInterval && !isNaN(debounceInterval)) {
          const timer = setTimeout(() => setBounceTimer(null), debounceInterval)
          setBounceTimer(timer)
        }
      } else {
        clearTimeout(bounceTimer)
        const timer = setTimeout(() => setBounceTimer(null), debounceInterval)
        setBounceTimer(timer)
      }
    },
    [token, execute, bounceTimer, debounceInterval, queuedArgs]
  )

  useEffect(() => {
    if (token && queuedArgs) {
      execute(...queuedArgs)
      setQueuedArgs(null)
    }
  }, [queuedArgs, token, execute])

  return fireAndForget
}
